import { useMemo, useState } from 'react';
import { Search } from '@/components/icons';
import { TemplateLogoStack } from '../../components/TemplateLogoStack';
import TemplatePreviewDialog from '../../components/TemplatePreviewDialog';
import {
  TEMPLATES,
  TEMPLATE_CATEGORIES,
  searchTemplates,
  getTemplatesByCategory,
  type Template,
  type TemplateCategory,
} from '../../data/templates';

const CATS: { id: TemplateCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  ...TEMPLATE_CATEGORIES.map((c) => ({ id: c.id, label: c.label })),
];

export default function Templates() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<TemplateCategory | 'all'>('all');
  const [preview, setPreview] = useState<Template | null>(null);

  const templates = useMemo(() => {
    if (query.trim()) return searchTemplates(query);
    if (category === 'all') return TEMPLATES;
    return getTemplatesByCategory(category);
  }, [query, category]);

  return (
    <div className="px-6 pb-16 pt-6 md:px-8">
      <div className="flex flex-col gap-[18px]">
        <div>
          <h1 className="text-[21px] font-semibold tracking-tight">Templates</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {TEMPLATES.length} starter architectures — pick one to start from.
          </p>
        </div>

        {/* Category filter + search */}
        <div className="flex flex-wrap items-center gap-2">
          {CATS.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`ss-press rounded-full border px-3.5 py-1.5 text-[12.5px] font-medium ${
                category === c.id && !query
                  ? 'border-transparent bg-foreground text-background'
                  : 'border-[var(--border-strong)] bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {c.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="flex h-[31px] w-[210px] items-center gap-2 rounded-md border border-transparent bg-muted px-2.5 text-[12.5px] text-muted-foreground focus-within:border-border focus-within:bg-card">
            <Search className="h-3.5 w-3.5 flex-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates"
              className="w-full bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Gallery */}
        {templates.length === 0 ? (
          <div className="rounded-xl border border-border bg-card px-4 py-20 text-center shadow-sm">
            <p className="text-[14px] font-semibold text-foreground">No templates match “{query}”</p>
            <p className="mt-1 text-[12.5px] text-muted-foreground">Try a different search or category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setPreview(t)}
                className="ss-press group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-sm hover:-translate-y-0.5 hover:shadow-md motion-reduce:hover:translate-y-0"
              >
                <div className="relative flex h-[150px] items-center justify-center overflow-hidden border-b border-border bg-muted">
                  <TemplateLogoStack template={t} size="lg" />
                  <div className="absolute inset-0 grid place-items-center bg-background/70 opacity-0 backdrop-blur-[1px] transition-opacity duration-150 ease-out group-hover:opacity-100">
                    <span className="rounded-md bg-foreground px-4 py-2 text-[12.5px] font-semibold text-background shadow-md">
                      Preview
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 p-3.5">
                  <b className="text-[14px] font-semibold tracking-tight">{t.name}</b>
                  <p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">{t.description}</p>
                  <div className="mt-0.5 flex flex-wrap gap-1.5">
                    {t.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-border px-2 py-0.5 font-mono text-[10.5px] text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <TemplatePreviewDialog template={preview} onClose={() => setPreview(null)} />
      </div>
    </div>
  );
}
